import type {
  FieldDefinition,
  TableScalarValue,
  TableSourceDescriptor,
  VectorSourceDescriptor,
} from '../data/model';

export interface JoinDiagnostics {
  readonly compatible: boolean;
  readonly incompatibility: string | null;
  readonly leftFeatureCount: number;
  readonly rightRowCount: number;
  readonly matchedFeatureCount: number;
  readonly unmatchedFeatureCount: number;
  readonly matchedTableRowCount: number;
  readonly unusedTableRowCount: number;
  readonly blankLeftKeyCount: number;
  readonly blankRightKeyCount: number;
  /** Number of distinct non-empty right-side key values which occur more than once. */
  readonly duplicateRightKeyCount: number;
  readonly duplicateRightKeySamples: readonly TableScalarValue[];
}

type JoinKeyKind = 'number' | 'string';

function fieldKind(field: FieldDefinition | undefined): JoinKeyKind | null {
  if (field?.type === 'integer' || field?.type === 'number') return 'number';
  return field?.type === 'string' ? 'string' : null;
}

function blank(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function lookupKey(value: unknown, kind: JoinKeyKind): string | null {
  if (blank(value)) return null;
  if (kind === 'number' && typeof value === 'number' && Number.isFinite(value)) {
    return `n:${Object.is(value, -0) ? 0 : value}`;
  }
  if (kind === 'string' && typeof value === 'string') return `s:${value}`;
  return null;
}

export function calculateJoinDiagnostics(
  vector: VectorSourceDescriptor,
  table: TableSourceDescriptor,
  leftFieldName: string,
  rightFieldName: string,
): JoinDiagnostics {
  const base = {
    leftFeatureCount: vector.featureCount,
    rightRowCount: table.rowCount,
  };
  const leftField = vector.fields.find((field) => field.name === leftFieldName);
  const rightColumn = table.fields.findIndex((field) => field.name === rightFieldName);
  const rightField = table.fields[rightColumn];
  const leftKind = fieldKind(leftField);
  const rightKind = fieldKind(rightField);
  const incompatibility =
    !leftField || !rightField
      ? 'Choose fields that still exist in both sources.'
      : !leftKind || !rightKind
        ? 'Join keys must be text or numeric fields.'
        : leftKind !== rightKind
          ? 'The layer and table key fields have incompatible types.'
          : null;
  if (incompatibility || !leftKind || rightColumn < 0) {
    return {
      ...base,
      compatible: false,
      incompatibility,
      matchedFeatureCount: 0,
      unmatchedFeatureCount: vector.featureCount,
      matchedTableRowCount: 0,
      unusedTableRowCount: table.rowCount,
      blankLeftKeyCount: 0,
      blankRightKeyCount: 0,
      duplicateRightKeyCount: 0,
      duplicateRightKeySamples: [],
    };
  }

  const rightRowsByKey = new Map<string, number[]>();
  let blankRightKeyCount = 0;
  for (let rowIndex = 0; rowIndex < table.rows.length; rowIndex += 1) {
    const value = table.rows[rowIndex]?.[rightColumn];
    const key = lookupKey(value, leftKind);
    if (key === null) {
      blankRightKeyCount += 1;
      continue;
    }
    const indices = rightRowsByKey.get(key);
    if (indices) indices.push(rowIndex);
    else rightRowsByKey.set(key, [rowIndex]);
  }
  const duplicateEntries = [...rightRowsByKey.entries()].filter(([, rows]) => rows.length > 1);
  const duplicateKeys = new Set(duplicateEntries.map(([key]) => key));
  const matchedRows = new Set<number>();
  let matchedFeatureCount = 0;
  let blankLeftKeyCount = 0;
  for (const feature of vector.data.features) {
    const value = feature.properties?.[leftFieldName];
    const key = lookupKey(value, leftKind);
    if (key === null) {
      blankLeftKeyCount += 1;
      continue;
    }
    const rows = rightRowsByKey.get(key);
    if (!rows || duplicateKeys.has(key)) continue;
    matchedFeatureCount += 1;
    matchedRows.add(rows[0] as number);
  }
  const duplicateRightKeySamples = duplicateEntries.slice(0, 5).flatMap(([, rows]) => {
    const row = table.rows[rows[0] as number];
    const value = row?.[rightColumn];
    return value === undefined ? [] : [value];
  });
  return {
    ...base,
    compatible: true,
    incompatibility: null,
    matchedFeatureCount,
    unmatchedFeatureCount: vector.featureCount - matchedFeatureCount,
    matchedTableRowCount: matchedRows.size,
    unusedTableRowCount: table.rowCount - matchedRows.size,
    blankLeftKeyCount,
    blankRightKeyCount,
    duplicateRightKeyCount: duplicateEntries.length,
    duplicateRightKeySamples,
  };
}

export function canApplyJoin(diagnostics: JoinDiagnostics, selectedFieldCount: number): boolean {
  return (
    diagnostics.compatible &&
    diagnostics.duplicateRightKeyCount === 0 &&
    diagnostics.matchedFeatureCount > 0 &&
    selectedFieldCount > 0
  );
}
